import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';

import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { interval, Subscription } from 'rxjs';
import { User } from '../../modals/user.model';
import { AuthService } from '../../services/auth.service';
import { CashDenomination, CashDenominationService } from '../../services/cash-denomination.service';
import { ExpenseType, PettyCash, PettyCashCreateRequest, PettyCashService } from '../../services/petty-cash.service';
import { PageHeaderComponent } from '../shared/components/page-header/page-header.component';

@Component({
  selector: 'app-petty-cash',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    DropdownModule,
    ToastModule,
    CardModule,
    ConfirmDialogModule,
    PageHeaderComponent,
  ],
  providers: [PettyCashService, MessageService, ConfirmationService],
  templateUrl: './petty-cash.component.html',
  styleUrl: './petty-cash.component.scss'
})
export class PettyCashComponent implements OnInit, OnDestroy {
  /** True inside Cash Management: no page header (the host page has one) and no page padding */
  @Input() @HostBinding('class.is-embedded') embedded = false;

  pettyCashData: PettyCash[] = [];
  pettyCashForm: FormGroup;
  openingBalance: number = 0;
  currentBalance: number = 0;
  isEditMode: boolean = false;
  editingId: string | number | null = null;
  currentDate: Date = new Date();
  currentUser: User | null = null;
  private authSubscription: Subscription = new Subscription();
  private timerSubscription: Subscription = new Subscription();
  dayInData: CashDenomination[] = [];

  typeOptions = [
    { label: 'Paid', value: 'paid' },
    { label: 'Received', value: 'received' }
  ];

  expenseTypes: ExpenseType[] = [];
  expenseTypeOptions: { label: string; value: number }[] = [];

  constructor(
    private pettyCashService: PettyCashService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private cashDenominationService: CashDenominationService,
  ) {
    this.pettyCashForm = this.fb.group({
      expensetype_id: [null, Validators.required],
      amount: [null, [Validators.required, Validators.min(1)]],
      type: ['paid', Validators.required],
      description: ['', Validators.required],
      entryBy: ['', Validators.required],
      cashFor: ['', Validators.required]
    });
  }
  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
    this.timerSubscription.unsubscribe();
  }

  ngOnInit() {
    // this.loadOpeningBalance();
    
    this.loadCashDenominationData();
    this.loadPettyCashData();
    this.loadExpenseTypes();
    this.subscribeToCurrentUser();

    this.timerSubscription = interval(1000).subscribe(() => {
      this.currentDate = new Date();
    });
  }

  
  subscribeToCurrentUser() {
    this.authSubscription = this.authService.currentUser.subscribe((user: User) => {
      this.currentUser = user;
      if (user && user.name) {
        this.pettyCashForm.patchValue({
          entryBy: user.name
        });
      }
    });
  }

 
  loadCashDenominationData() {
    let dayInLoaded = false;
    let dayOutLoaded = false;

    // Load Day In data
    this.cashDenominationService.getDayInCashDenominations().subscribe({
      next: (response) => {
        if (response.status === 200 && response.data) {
          this.dayInData = response.data;
          this.dayInData.forEach(data => {
            this.openingBalance += data.amount*data.quantity
          })
        } else {
          this.dayInData = [];
        }
      },
      error: () => {
        this.dayInData = [];
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Day In data'
        });
      }
    });

  }



  loadPettyCashData() {
    this.pettyCashService.getPettyCash().subscribe({
      next: (response) => {
        if (response.status === 200 && response.datas) {
          // Map API response to component format and sort by date
          this.pettyCashData = response.datas.map(item => ({
            ...item,
            // Map API fields to legacy fields for backward compatibility
            //type: item.expensetype?.name === 'Paid' ? 'Paid' as const : 'Received' as const,
            type: item.paytype == 0 ? 'Paid' as const : 'Received' as const,
            entryBy: `User ${item.createdBy}`,
            cashFor: item.expensetype?.name || 'General',
            date: item.expensedate || item.createdAt || new Date().toISOString()
          })).sort((a, b) => new Date(a.date || '').getTime() - new Date(b.date || '').getTime());

          this.calculateCurrentBalance();
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load petty cash data'
        });
      }
    });
  }

  loadExpenseTypes() {
    this.pettyCashService.getExpenseTypes().subscribe({
      next: (response) => {
        if (response.status === 200) {
          this.expenseTypes = response.data.filter(type => type.status === 1); // Only active expense types
          this.expenseTypeOptions = this.expenseTypes.map(type => ({
            label: type.name,
            value: type.id
          }));
        }
      },
      error: (error) => {
        console.error('Error loading expense types:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load expense categories'
        });
      }
    });
  }

  calculateCurrentBalance() {
    let balance = this.openingBalance; 
    for (const transaction of this.pettyCashData) {
      let createdAt = transaction.createdAt as string
      var inputDate = new Date(createdAt);
      // Get today's date
      var todaysDate = new Date();

      if(inputDate.setHours(0,0,0,0) == todaysDate.setHours(0,0,0,0)) { 
          if (transaction.type === 'Paid' && transaction.createdAt) {
            balance -= transaction.amount;
          } else {
            balance += transaction.amount;
          }
      }
    }
    this.currentBalance = balance;
  }

  getRunningBalance(index: number): number {
    let balance = this.openingBalance;
    for (let i = 0; i <= index; i++) {
      const transaction = this.pettyCashData[i];
      if (transaction.type === 'Paid' || transaction.type === 'paid') {
        balance -= transaction.amount;
      } else {
        balance += transaction.amount;
      }
    }
    return balance;
  }

  getExpenseTypeName(expenseTypeId: number): string {
    const expenseType = this.expenseTypes.find(type => type.id === expenseTypeId);
    return expenseType ? expenseType.name : 'Unknown';
  }

  onSubmit() {
    if (this.pettyCashForm.valid) {
      const formData = this.pettyCashForm.value;
      const newEntry: PettyCashCreateRequest = {
        expensetype_id: formData.expensetype_id,
        amount: formData.amount,
        description: formData.description,
        expenseType: formData.type,
        cashFor: formData.cashFor,
        entryTimestamp: new Date().toISOString(),
        entryBy: formData.entryBy
      };

      if (this.isEditMode && this.editingId) {
        // For edit mode, still use the old format for now
        const legacyEntry: PettyCash = {
          amount: formData.amount,
          description: formData.description,
          expensedate: new Date().toISOString().split('T')[0],
          expensetype_id: formData.expensetype_id,
          bankaccount_id: 1,
          status: 1,
          createdBy: 1,
          warehouse_id: 1,
          type: formData.type,
          entryBy: formData.entryBy,
          cashFor: formData.cashFor,
          date: new Date().toISOString()
        };
        this.updatePettyCash(this.editingId.toString(), legacyEntry);
      } else {
        this.createPettyCashNew(newEntry);
      }
    }
  }

  createPettyCash(entry: PettyCash) {
    this.pettyCashService.createPettyCash(entry).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Petty cash entry added successfully'
        });
        this.resetForm();
        this.loadPettyCashData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add petty cash entry'
        });
      }
    });
  }

  createPettyCashNew(entry: PettyCashCreateRequest) {
    this.pettyCashService.createPettyCashNew(entry).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Petty cash entry added successfully'
        });
        this.resetForm();
        this.loadPettyCashData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to add petty cash entry'
        });
      }
    });
  }

  updatePettyCash(id: string, entry: PettyCash) {
    this.pettyCashService.updatePettyCash(id, entry).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Petty cash entry updated successfully'
        });
        this.resetForm();
        this.loadPettyCashData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update petty cash entry'
        });
      }
    });
  }

  editEntry(entry: PettyCash) {
    this.isEditMode = true;
    this.editingId = entry.uuid!;

    // Map old type values to new format
    const mappedType = entry.type === 'Paid' ? 'paid' :
                      entry.type === 'Received' ? 'received' :
                      entry.type === 'paid' ? 'paid' :
                      entry.type === 'received' ? 'received' : 'paid';

    this.pettyCashForm.patchValue({
      expensetype_id: entry.expensetype_id,
      amount: entry.amount,
      type: mappedType,
      description: entry.description,
      entryBy: entry.entryBy,
      cashFor: entry.cashFor
    });
  }

  deleteEntry(id: string | number) {
    // Find the entry to get the complete data for payload
    const entryToDelete = this.pettyCashData.find(entry =>
      entry.id?.toString() === id.toString() || entry.uuid?.toString() === id.toString()
    );

    if (!entryToDelete) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Entry not found'
      });
      return;
    }

    // Show confirmation dialog
    this.confirmationService.confirm({
      message: `Are you sure you want to delete this petty cash entry of ${entryToDelete.amount | 0}?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.performDelete(id, entryToDelete);
      }
    });
  }

  private performDelete(id: string | number, entryToDelete: PettyCash) {

    // Create payload similar to update
    const deletePayload: PettyCash = {
      amount: entryToDelete.amount,
      description: entryToDelete.description,
      expensedate: entryToDelete.expensedate || new Date().toISOString().split('T')[0],
      expensetype_id: entryToDelete.expensetype_id,
      bankaccount_id: entryToDelete.bankaccount_id || 1,
      status: entryToDelete.status || 1,
      createdBy: entryToDelete.createdBy || 1,
      warehouse_id: entryToDelete.warehouse_id || 1,
      // Legacy fields for backward compatibility
      type: entryToDelete.type,
      entryBy: entryToDelete.entryBy,
      cashFor: entryToDelete.cashFor,
      date: entryToDelete.date || new Date().toISOString()
    };

    this.pettyCashService.deletePettyCash(id.toString(), deletePayload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Petty cash entry deleted successfully'
        });
        this.loadPettyCashData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete petty cash entry'
        });
      }
    });
  }

  resetForm() {
    this.isEditMode = false;
    this.editingId = null;
    this.pettyCashForm.reset({
      type: 'paid',
      entryBy: this.currentUser?.name || '',
      expensetype_id: null
    });
  }

  addMore() {
    this.resetForm();
  }

  goBackToHome() {
    this.router.navigate(['/dashboard']);
  }
}
